"use client"

import * as React from "react"
import { useRef, useState, useEffect, forwardRef } from "react"
import { ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { CountryFlag } from "./country-flags"

interface CustomSelectOption {
  value: string
  label: string
  country?: string
  code?: string // Country code for flag
}

interface CustomSelectProps {
  options: CustomSelectOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  className?: string
  triggerClassName?: string
}

export const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>(
  ({ options, value, onValueChange, placeholder, className, triggerClassName }, forwardedRef) => {
    const [open, setOpen] = useState(false)
    const selectRef = useRef<HTMLDivElement>(null)

    // Close the select when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
          setOpen(false)
        }
      }

      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }, [])

    // Get the label for the current value
    const selectedLabel = options.find(option => option.value === value)?.label || placeholder || value

    return (
      <div 
        className={cn("relative", className)} 
        ref={selectRef}
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(!open)}
          className={cn(
            "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
            triggerClassName
          )}
          aria-expanded={open}
        >
          <span>{selectedLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
        
        {open && (
          <div className="absolute z-50 w-auto min-w-[16rem] overflow-hidden rounded-md border bg-white text-gray-900 shadow-lg animate-in fade-in-0 zoom-in-95 mt-1">
            <div className="p-1 max-h-60 overflow-auto">
              {options?.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center gap-2 rounded-sm py-2 px-3 text-sm outline-none hover:bg-gray-100",
                    option.value === value && "bg-gray-100 font-medium"
                  )}
                  onClick={() => {
                    onValueChange(option.value)
                    setOpen(false)
                  }}
                >
                  {option.code && option.country && (
                    <CountryFlag code={option.code} country={option.country} />
                  )}
                  <span className="font-medium">{option.label}</span>
                  {option.country && (
                    <span className="text-gray-500 text-xs ml-1">({option.country})</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }
)

CustomSelect.displayName = "CustomSelect"
